if (!customElements.get("hot-products-carousel")) {
    class HotProductsCarousel extends HTMLElement {
        constructor() {
            super();

            this.viewport = this.querySelector("[data-hot-products-viewport]");
            this.track = this.querySelector("[data-hot-products-track]");
            this.items = Array.from(
                this.querySelectorAll("[data-hot-product-slide]")
            );
            this.controls = this.querySelector("[data-hot-products-controls]");
            this.previousButton = this.querySelector(
                "[data-hot-products-previous]"
            );
            this.nextButton = this.querySelector("[data-hot-products-next]");

            this.currentIndex = 0;
            this.frameRequested = false;
            this.isPointerDown = false;
            this.hasDragged = false;
            this.suppressClick = false;

            this.autoplayInterval =
                parseFloat(this.dataset.autoplay || "0") * 1000;
            this.transitionDuration = parseFloat(
                this.dataset.transitionDuration || "500"
            );
            this.loop = this.dataset.loop === "true";
            this.pauseOnHover = this.dataset.pauseOnHover === "true";
            this.reducedMotion = window.matchMedia(
                "(prefers-reduced-motion: reduce)"
            ).matches;

            this.onPreviousClick = () => this.changeSlide(-1, true);
            this.onNextClick = () => this.changeSlide(1, true);
            this.onScroll = this.requestStateUpdate.bind(this);
            this.onResize = this.requestStateUpdate.bind(this);
            this.onPointerDown = this.handlePointerDown.bind(this);
            this.onPointerMove = this.handlePointerMove.bind(this);
            this.onPointerUp = this.handlePointerUp.bind(this);
            this.onClick = this.handleClick.bind(this);
            this.onMouseEnter = () => this.pauseOnHover && this.stop();
            this.onMouseLeave = () => this.play();
            this.onFocusIn = () => this.stop();
            this.onFocusOut = (event) => {
                if (!this.contains(event.relatedTarget)) this.play();
            };
            this.onVisibilityChange = () => {
                document.hidden ? this.stop() : this.play();
            };
            this.onBlockSelect = this.handleBlockSelect.bind(this);
            this.onBlockDeselect = () => this.play();
            this.onDragStart = (event) => event.preventDefault();
        }

        connectedCallback() {
            if (!this.viewport || !this.track || this.items.length === 0) return;

            this.previousButton?.addEventListener(
                "click",
                this.onPreviousClick
            );
            this.nextButton?.addEventListener("click", this.onNextClick);
            this.viewport.addEventListener("scroll", this.onScroll, {
                passive: true,
            });
            this.viewport.addEventListener("pointerdown", this.onPointerDown);
            this.viewport.addEventListener("pointermove", this.onPointerMove);
            this.viewport.addEventListener("pointerup", this.onPointerUp);
            this.viewport.addEventListener("pointercancel", this.onPointerUp);
            this.viewport.addEventListener("click", this.onClick, true);
            this.viewport.addEventListener("dragstart", this.onDragStart);
            this.addEventListener("mouseenter", this.onMouseEnter);
            this.addEventListener("mouseleave", this.onMouseLeave);
            this.addEventListener("focusin", this.onFocusIn);
            this.addEventListener("focusout", this.onFocusOut);
            document.addEventListener(
                "visibilitychange",
                this.onVisibilityChange
            );

            if (window.Shopify?.designMode) {
                document.addEventListener(
                    "shopify:block:select",
                    this.onBlockSelect
                );
                document.addEventListener(
                    "shopify:block:deselect",
                    this.onBlockDeselect
                );
            }

            if ("ResizeObserver" in window) {
                this.resizeObserver = new ResizeObserver(this.onResize);
                this.resizeObserver.observe(this.viewport);
            } else {
                window.addEventListener("resize", this.onResize);
            }

            this.updateState();
            this.play();
        }

        disconnectedCallback() {
            this.stop();
            cancelAnimationFrame(this.animationFrame);

            this.previousButton?.removeEventListener(
                "click",
                this.onPreviousClick
            );
            this.nextButton?.removeEventListener("click", this.onNextClick);
            this.viewport?.removeEventListener("scroll", this.onScroll);
            this.viewport?.removeEventListener(
                "pointerdown",
                this.onPointerDown
            );
            this.viewport?.removeEventListener(
                "pointermove",
                this.onPointerMove
            );
            this.viewport?.removeEventListener("pointerup", this.onPointerUp);
            this.viewport?.removeEventListener(
                "pointercancel",
                this.onPointerUp
            );
            this.viewport?.removeEventListener("click", this.onClick, true);
            this.viewport?.removeEventListener("dragstart", this.onDragStart);
            this.removeEventListener("mouseenter", this.onMouseEnter);
            this.removeEventListener("mouseleave", this.onMouseLeave);
            this.removeEventListener("focusin", this.onFocusIn);
            this.removeEventListener("focusout", this.onFocusOut);
            document.removeEventListener(
                "visibilitychange",
                this.onVisibilityChange
            );
            document.removeEventListener(
                "shopify:block:select",
                this.onBlockSelect
            );
            document.removeEventListener(
                "shopify:block:deselect",
                this.onBlockDeselect
            );
            this.resizeObserver?.disconnect();
            window.removeEventListener("resize", this.onResize);
        }

        get isRtl() {
            return getComputedStyle(this.viewport).direction === "rtl";
        }

        get visibleCount() {
            const firstItem = this.items[0];
            if (!firstItem) return 1;

            const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
            return Math.max(
                1,
                Math.round(
                    (this.viewport.clientWidth + gap) /
                        (firstItem.clientWidth + gap)
                )
            );
        }

        get maxIndex() {
            return Math.max(0, this.items.length - this.visibleCount);
        }

        requestStateUpdate() {
            if (this.frameRequested) return;

            this.frameRequested = true;
            requestAnimationFrame(() => {
                this.frameRequested = false;
                this.updateState();
            });
        }

        updateState() {
            const viewportRect = this.viewport.getBoundingClientRect();
            const viewportStart = this.isRtl
                ? viewportRect.right
                : viewportRect.left;
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            this.items.forEach((item, index) => {
                const itemRect = item.getBoundingClientRect();
                const itemStart = this.isRtl ? itemRect.right : itemRect.left;
                const distance = Math.abs(itemStart - viewportStart);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            this.currentIndex = Math.min(closestIndex, this.maxIndex);

            const hasOverflow =
                this.viewport.scrollWidth > this.viewport.clientWidth + 1;
            this.classList.toggle("is-overflowing", hasOverflow);
            if (this.controls) this.controls.hidden = !hasOverflow;

            if (this.loop) {
                if (this.previousButton) this.previousButton.disabled = false;
                if (this.nextButton) this.nextButton.disabled = false;
            } else {
                if (this.previousButton) {
                    this.previousButton.disabled = this.currentIndex <= 0;
                }
                if (this.nextButton) {
                    this.nextButton.disabled =
                        this.currentIndex >= this.maxIndex;
                }
            }
        }

        changeSlide(step, userInitiated = false) {
            if (this.maxIndex === 0) return;

            let targetIndex = this.currentIndex + step;
            if (this.loop) {
                if (targetIndex > this.maxIndex) targetIndex = 0;
                if (targetIndex < 0) targetIndex = this.maxIndex;
            } else {
                targetIndex = Math.min(
                    this.maxIndex,
                    Math.max(0, targetIndex)
                );
            }

            this.scrollToIndex(targetIndex);

            if (userInitiated) {
                this.stop();
                this.play();
            }
        }

        scrollToIndex(index) {
            const item = this.items[index];
            if (!item) return;

            cancelAnimationFrame(this.animationFrame);
            this.classList.remove("is-animating");

            const start = this.viewport.scrollLeft;

            // Let the browser resolve the logical inline start. This avoids
            // relying on browser-specific positive/negative RTL scrollLeft
            // models, while still allowing a configurable animation speed.
            item.scrollIntoView({
                behavior: "instant",
                block: "nearest",
                inline: "start",
            });
            const target = this.viewport.scrollLeft;
            this.viewport.scrollLeft = start;
            const distance = target - start;

            if (this.reducedMotion || this.transitionDuration <= 0) {
                this.viewport.scrollLeft = target;
                this.currentIndex = index;
                this.updateState();
                return;
            }

            const startedAt = performance.now();
            this.classList.add("is-animating");

            const animate = (now) => {
                const progress = Math.min(
                    1,
                    (now - startedAt) / this.transitionDuration
                );
                const eased = 1 - Math.pow(1 - progress, 3);
                this.viewport.scrollLeft = start + distance * eased;

                if (progress < 1) {
                    this.animationFrame = requestAnimationFrame(animate);
                } else {
                    this.classList.remove("is-animating");
                    this.viewport.scrollLeft = target;
                    this.currentIndex = index;
                    this.updateState();
                }
            };

            this.animationFrame = requestAnimationFrame(animate);
        }

        handlePointerDown(event) {
            this.stop();
            if (event.pointerType !== "mouse" || event.button !== 0) return;

            this.isPointerDown = true;
            this.hasDragged = false;
            this.startX = event.clientX;
            this.startScrollLeft = this.viewport.scrollLeft;
            this.pointerIsRtl = this.isRtl;
        }

        handlePointerMove(event) {
            if (!this.isPointerDown) return;

            const distance = event.clientX - this.startX;
            if (!this.hasDragged && Math.abs(distance) < 5) return;

            if (!this.hasDragged) {
                this.hasDragged = true;
                this.viewport.setPointerCapture(event.pointerId);
            }
            this.classList.add("is-dragging");
            event.preventDefault();
            this.viewport.scrollLeft =
                this.startScrollLeft +
                (this.pointerIsRtl ? distance : -distance);
        }

        handlePointerUp(event) {
            if (event.pointerType !== "mouse") {
                this.play();
                return;
            }
            if (!this.isPointerDown) return;

            this.isPointerDown = false;
            this.suppressClick = this.hasDragged;
            this.classList.remove("is-dragging");

            if (this.viewport.hasPointerCapture(event.pointerId)) {
                this.viewport.releasePointerCapture(event.pointerId);
            }

            this.updateState();
            this.play();
            window.setTimeout(() => {
                this.suppressClick = false;
            }, 100);
        }

        handleClick(event) {
            if (!this.suppressClick) return;

            event.preventDefault();
            event.stopPropagation();
        }

        handleBlockSelect(event) {
            const item = event.target.closest?.("[data-hot-product-slide]");
            if (!item || !this.contains(item)) return;

            this.stop();
            const index = this.items.indexOf(item);
            if (index >= 0) this.scrollToIndex(Math.min(index, this.maxIndex));
        }

        play() {
            this.stop();
            if (
                !this.autoplayInterval ||
                this.reducedMotion ||
                document.hidden ||
                this.maxIndex === 0
            ) {
                return;
            }

            this.autoplayTimer = window.setInterval(() => {
                if (!this.loop && this.currentIndex >= this.maxIndex) {
                    this.stop();
                    return;
                }
                this.changeSlide(1);
            }, this.autoplayInterval);
        }

        stop() {
            window.clearInterval(this.autoplayTimer);
        }
    }

    customElements.define("hot-products-carousel", HotProductsCarousel);
}
